/**
 * Main App Component with Routing
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './lib/auth-store';
import useThemeStore from './lib/theme-store';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LeadsPage from './pages/LeadsPage';
import AddLeadPage from './pages/AddLeadPage';
import LeadDetailPage from './pages/LeadDetailPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationWizardPage from './pages/ApplicationWizardPage';
import CalendarPage from './pages/CalendarPage';
import DocumentsPage from './pages/DocumentsPage';
import ELearningPage from './pages/ELearningPage';
import ClassroomPage from './pages/ClassroomPage';

// Admin Pages
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import TenantManagement from './pages/admin/TenantManagement';
import UserManagement from './pages/admin/UserManagement';
import AuditLogs from './pages/admin/AuditLogs';
import MerchantApprovals from './pages/admin/MerchantApprovals';

// ISO / Shared Pages
import ISOUserManagement from './pages/admin/ISOUserManagement';
import TenantSettings from './pages/admin/TenantSettings';
import PaymentsPage from './pages/admin/PaymentsPage';

// Residuals Module
import SubUserManagement from './pages/SubUserManagement';
import CommissionCalculator from './pages/CommissionCalculator';
import ResidualSettings from './pages/admin/ResidualSettings';
import UserDetailPage from './pages/admin/UserDetailPage';
import UserResidualsView from './pages/UserResidualsView';
import ScheduleAPage from './pages/ScheduleAPage';
import MenuLinksAdmin from './pages/admin/MenuLinksAdmin';
import EmbedCustomLink from './pages/EmbedCustomLink';
import HardwareMarkup from './pages/admin/HardwareMarkup';

// Equipment
import EquipmentCatalog from './pages/EquipmentCatalog';

// Analytics
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';

// User Settings & Help
import UserSettingsPage from './pages/UserSettingsPage';
import HelpSupportPage from './pages/HelpSupportPage';

// Pipeline
import PipelinePage from './pages/PipelinePage';

// Orders
import OrdersManagementPage from './pages/OrdersManagementPage';

// Maverick
import MaverickPage from './pages/MaverickPage';

// Activity Logs
import ActivityLogsPage from './pages/ActivityLogsPage';

// Lead Forms
import FormBuilderPage from './pages/FormBuilderPage';
import AffiliatesPage from './pages/AffiliatesPage';
import PublicFormPage from './pages/PublicFormPage';
import ConversionFunnelPage from './pages/ConversionFunnelPage';
import StatementAnalysisPage from './pages/StatementAnalysisPage';
import ProposalViewPage from './pages/ProposalViewPage';
import SignupPage from './pages/SignupPage';
import ActivationPage from './pages/ActivationPage';
import TeamInvitePage from './pages/TeamInvitePage';
import FeatureGuidePage from './pages/FeatureGuidePage';
import EnrollmentRequestsPage from './pages/admin/EnrollmentRequestsPage';
import AgreementsAdminPage from './pages/admin/AgreementsAdminPage';
import ScheduleAMasterPage from './pages/admin/ScheduleAMasterPage';
import CostTemplatesPage from './pages/admin/CostTemplatesPage';
import EinReviewsPage from './pages/admin/EinReviewsPage';
import ResidualAnalysisPage from './pages/admin/ResidualAnalysisPage';
import MyResidualAnalysisPage from './pages/MyResidualAnalysisPage';
import ActivationTrackerPage from './pages/ActivationTrackerPage';
import ReplyCoachPage from './pages/ReplyCoachPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import OnboardPartnerPage from './pages/admin/OnboardPartnerPage';
import PartnerBillingPage from './pages/admin/PartnerBillingPage';
import BillingPage from './pages/BillingPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages';
import AnnouncementsAdminPage from './pages/admin/AnnouncementsAdminPage';
import ImageStudioPage from './pages/admin/ImageStudioPage';
import SampleDataCleanupPage from './pages/admin/SampleDataCleanupPage';
import AnnouncementsPage from './components/comm/Announcements';
import AgreementCenterPage from './pages/AgreementCenterPage';
import AgentTermsPage from './pages/AgentTermsPage';
import PayoutResponsibilityPage from './pages/admin/PayoutResponsibilityPage';
import PayerPayoutRunsPage from './pages/admin/PayerPayoutRunsPage';
import MyPayoutRunPage from './pages/MyPayoutRunPage';
import EconomicRelationshipsPage from './pages/admin/EconomicRelationshipsPage';
import MyManagedAgentsPage from './pages/MyManagedAgentsPage';
import JarvisKnowledgePage from './pages/admin/JarvisKnowledgePage';
import PaymentProgramsPage from './pages/admin/PaymentProgramsPage';
import CandidatePortalPage from './pages/CandidatePortalPage';
import CommunicationsPage from './pages/CommunicationsPage';
import { GoogleHashGate } from './components/auth/GoogleSignIn';
import ProvisioningPage from './pages/admin/ProvisioningPage';
import TenantProfilePage from './pages/admin/TenantProfilePage';
import TeamMemberEconomicsPage from './pages/TeamMemberEconomicsPage';
import NearbyLeadsPage from './pages/NearbyLeadsPage';
import DemoVerifyPage from './pages/DemoVerifyPage';

// Merchant Vault
import MerchantsPage from './pages/MerchantsPage';
import MerchantDetailPage from './pages/MerchantDetailPage';

// E-Signature
import ESignaturePage from './pages/ESignaturePage';

// Prospecting Agent
import ProspectingPage from './pages/ProspectingPage';
import SalesGPSPage from './pages/SalesGPSPage';

// Tasks
import TasksPage from './pages/TasksPage';
import WeeklyPlannerPage from './pages/WeeklyPlannerPage';
import KYCQueuePage from './pages/KYCQueuePage';
import IdentityReviewPage from './pages/IdentityReviewPage';
import MidHealthPage from './pages/MidHealthPage';
import PayerEconomicsPage from './pages/PayerEconomicsPage';
import PayoutRunPage from './pages/PayoutRunPage';
import ScheduleApprovalsPage from './pages/ScheduleApprovalsPage';
import ReconciliationPage from './pages/ReconciliationPage';
import PaymentProfilesPage from './pages/PaymentProfilesPage';
import SystemHealthPage from './pages/SystemHealthPage';

// Public Statement Upload
import StatementUploadPage from './pages/StatementUploadPage';

import ReferralPartnerPage from './pages/ReferralPartnerPage';
import AcceptAffiliateInvitePage from './pages/AcceptAffiliateInvitePage';
import AffiliatePortalPage from './pages/AffiliatePortalPage';

import BoardingApplicationPage from './pages/BoardingApplicationPage';
import ProcessorConnectionsPage from './pages/ProcessorConnectionsPage';

// Layout
import AppLayout from './components/AppLayout';
import { DemoTourProvider } from './components/tour/DemoTourContext';

// Super Admin Route Guard - redirects non-super_admin users to dashboard
function SuperAdminRoute({ children }) {
  const { user } = useAuthStore();
  if (user?.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  const { isAuthenticated, isLoading, loadUser, user } = useAuthStore();
  const { initTheme } = useThemeStore();

  useEffect(() => {
    initTheme();
    loadUser();
  }, [loadUser, initTheme]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const isAffiliate = user?.role === 'affiliate';
  const defaultRoute = user?.role === 'super_admin' ? '/admin' : isAffiliate ? '/affiliate' : '/';

  return (
    <BrowserRouter>
      <Toaster richColors position="top-right" />
      <GoogleHashGate>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={!isAuthenticated ? <LoginPage /> : <Navigate to={defaultRoute} replace />}
        />
        <Route path="/forms/:slug" element={<PublicFormPage />} />
        <Route path="/proposals/view/:token" element={<ProposalViewPage />} />
        <Route path="/onboarding" element={<SignupPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/candidate" element={isAuthenticated && user?.role === 'candidate' ? <CandidatePortalPage /> : <Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
        <Route path="/demo-verify" element={<DemoVerifyPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/affiliate/accept/:token" element={<AcceptAffiliateInvitePage />} />
        <Route path="/affiliate" element={isAuthenticated && isAffiliate ? <AffiliatePortalPage /> : <Navigate to={isAuthenticated ? '/' : '/login'} replace />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={isAuthenticated && user?.role === 'candidate' ? <Navigate to="/candidate" replace /> : isAuthenticated && isAffiliate ? <Navigate to="/affiliate" replace /> : isAuthenticated ? (
            <DemoTourProvider enabled={true}>
              <AppLayout />
            </DemoTourProvider>
          ) : <Navigate to="/login" replace />}
        >
          <Route index element={<DashboardPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="planner" element={<WeeklyPlannerPage />} />
          <Route path="kyc-queue" element={<KYCQueuePage />} />
          <Route path="duplicate-review" element={<IdentityReviewPage />} />
          <Route path="mid-health" element={<MidHealthPage />} />
          <Route path="payer-economics" element={<PayerEconomicsPage />} />
          <Route path="payout-run" element={<PayoutRunPage />} />
          <Route path="schedule-approvals" element={<ScheduleApprovalsPage />} />
          <Route path="reconciliation" element={<ReconciliationPage />} />
          <Route path="payment-profiles" element={<PaymentProfilesPage />} />
          <Route path="system-health" element={<SystemHealthPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="leads/new" element={<AddLeadPage />} />
          <Route path="leads/:leadId" element={<LeadDetailPage />} />
          <Route path="merchants" element={<MerchantsPage />} />
          <Route path="merchants/:groupId" element={<MerchantDetailPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="applications/:appId" element={<ApplicationWizardPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="elearning" element={<ELearningPage />} />
          <Route path="elearning/:pathId" element={<ClassroomPage />} />
          <Route path="equipment" element={<EquipmentCatalog />} />

          {/* Super Admin Routes - protected by role check */}
          <Route path="admin" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
          <Route path="admin/tenants" element={<SuperAdminRoute><TenantManagement /></SuperAdminRoute>} />
          <Route path="admin/tenants/:tenantId" element={<SuperAdminRoute><TenantProfilePage /></SuperAdminRoute>} />
          <Route path="team/:userId" element={<TeamMemberEconomicsPage />} />
          <Route path="admin/users" element={<SuperAdminRoute><UserManagement /></SuperAdminRoute>} />
          <Route path="admin/audit-logs" element={<SuperAdminRoute><AuditLogs /></SuperAdminRoute>} />
          <Route path="admin/merchant-approvals" element={<SuperAdminRoute><MerchantApprovals /></SuperAdminRoute>} />
          <Route path="admin/residuals" element={<SuperAdminRoute><ResidualSettings /></SuperAdminRoute>} />
          <Route path="admin/users/:userId" element={<SuperAdminRoute><UserDetailPage /></SuperAdminRoute>} />
          <Route path="admin/hardware-markup" element={<SuperAdminRoute><HardwareMarkup /></SuperAdminRoute>} />
          <Route path="admin/analytics" element={<SuperAdminRoute><AnalyticsDashboard /></SuperAdminRoute>} />

          {/* ISO / Shared Routes */}
          <Route path="users" element={<ISOUserManagement />} />
          <Route path="settings" element={<TenantSettings />} />
          <Route path="my-settings" element={<UserSettingsPage />} />
          <Route path="help" element={<HelpSupportPage />} />
          <Route path="help/feature-guide" element={<FeatureGuidePage />} />
          <Route path="activate" element={<ActivationPage />} />
          <Route path="agreement" element={<AgreementCenterPage />} />
          <Route path="agent-terms" element={<AgentTermsPage />} />
          <Route path="team-invite" element={<TeamInvitePage />} />
          <Route path="admin/enrollment" element={<SuperAdminRoute><EnrollmentRequestsPage /></SuperAdminRoute>} />
          <Route path="admin/agreements" element={<SuperAdminRoute><AgreementsAdminPage /></SuperAdminRoute>} />
          <Route path="admin/schedule-a-master" element={<SuperAdminRoute><ScheduleAMasterPage /></SuperAdminRoute>} />
          <Route path="admin/schedule-a-templates" element={<SuperAdminRoute><CostTemplatesPage /></SuperAdminRoute>} />
          <Route path="admin/ein-reviews" element={<SuperAdminRoute><EinReviewsPage /></SuperAdminRoute>} />
          <Route path="admin/residual-analyses" element={<SuperAdminRoute><ResidualAnalysisPage /></SuperAdminRoute>} />
          <Route path="my-residual-analysis" element={<MyResidualAnalysisPage />} />
          <Route path="activation-tracker" element={<ActivationTrackerPage />} />
          <Route path="reply-coach" element={<ReplyCoachPage />} />
          <Route path="admin/team" element={<SuperAdminRoute><AdminTeamPage /></SuperAdminRoute>} />
          <Route path="admin/onboard-partner" element={<SuperAdminRoute><OnboardPartnerPage /></SuperAdminRoute>} />
          <Route path="admin/partner-billing" element={<SuperAdminRoute><PartnerBillingPage /></SuperAdminRoute>} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="admin/announcements" element={<SuperAdminRoute><AnnouncementsAdminPage /></SuperAdminRoute>} />
          <Route path="admin/image-studio" element={<SuperAdminRoute><ImageStudioPage /></SuperAdminRoute>} />
          <Route path="admin/sample-data" element={<SuperAdminRoute><SampleDataCleanupPage /></SuperAdminRoute>} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="admin/payout-responsibility" element={<SuperAdminRoute><PayoutResponsibilityPage /></SuperAdminRoute>} />
          <Route path="admin/payer-payout-runs" element={<SuperAdminRoute><PayerPayoutRunsPage /></SuperAdminRoute>} />
          <Route path="my-payout-run" element={<MyPayoutRunPage />} />
          <Route path="admin/economic-relationships" element={<SuperAdminRoute><EconomicRelationshipsPage /></SuperAdminRoute>} />
          <Route path="my-managed-agents" element={<MyManagedAgentsPage />} />
          <Route path="admin/jarvis-knowledge" element={<SuperAdminRoute><JarvisKnowledgePage /></SuperAdminRoute>} />
          <Route path="communications" element={<CommunicationsPage />} />
          <Route path="admin/payment-programs" element={<SuperAdminRoute><PaymentProgramsPage /></SuperAdminRoute>} />
          <Route path="admin/provisioning" element={<SuperAdminRoute><ProvisioningPage /></SuperAdminRoute>} />
          <Route path="pipeline/:pipelineType" element={<PipelinePage />} />
          <Route path="orders" element={<OrdersManagementPage />} />
          <Route path="processor" element={<MaverickPage />} />
          <Route path="esignatures" element={<ESignaturePage />} />
          <Route path="esign/complete" element={<ESignaturePage />} />
          <Route path="prospecting" element={<ProspectingPage />} />
          <Route path="sales-gps" element={<SalesGPSPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          {/* Residuals - Calculator is Super Admin Only */}
          <Route path="residuals" element={<UserResidualsView />} />
          <Route path="schedule-a" element={<ScheduleAPage />} />
          <Route path="admin/menu-links" element={<MenuLinksAdmin />} />
          <Route path="embed/:linkId" element={<EmbedCustomLink />} />
          <Route path="residuals/sub-users" element={<SubUserManagement />} />
          <Route path="residuals/calculator" element={<SuperAdminRoute><CommissionCalculator /></SuperAdminRoute>} />
          <Route path="activity-logs" element={<ActivityLogsPage />} />
          <Route path="leads/form-builder" element={<FormBuilderPage />} />
          <Route path="affiliates" element={<AffiliatesPage />} />
          <Route path="affiliates/:affiliateId" element={<ReferralPartnerPage />} />
          <Route path="boarding/:appId" element={<BoardingApplicationPage />} />
          <Route path="processor-connections" element={<ProcessorConnectionsPage />} />
          <Route path="funnel" element={<ConversionFunnelPage />} />
          <Route path="statement-analysis" element={<StatementAnalysisPage />} />
          <Route path="map" element={<NearbyLeadsPage />} />
        </Route>

        {/* Public Statement Upload - No Auth Required */}
        <Route path="/upload-statement/:token" element={<StatementUploadPage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </GoogleHashGate>
    </BrowserRouter>
  );
}

export default App;
